import "@hotwired/turbo-rails"
import { Application, Controller } from "@hotwired/stimulus"
import MarketCandlesController from "./controllers/market_candles_controller"
import PerformanceChartController from "./controllers/performance_chart_controller"

class RefreshController extends Controller {
  static values = { interval: { type: Number, default: 60_000 } }
  static targets = ["frame"]
  declare intervalValue: number
  declare readonly frameTargets: TurboFrameElement[]
  private timer?: number
  private readonly scrollStorageKey = "stonks:auto-refresh-scroll"

  connect() {
    this.restoreScroll()
    this.timer = window.setInterval(() => {
      if (document.visibilityState === "visible") this.refresh()
    }, this.intervalValue)
  }

  disconnect() {
    if (this.timer) window.clearInterval(this.timer)
  }

  private refresh() {
    if (this.frameTargets.length > 0) {
      this.frameTargets.forEach((frame) => {
        const refreshUrl = frame.dataset.refreshUrl || window.location.href
        if (frame.src) {
          frame.reload()
        } else {
          frame.src = refreshUrl
        }
      })
      return
    }

    window.sessionStorage.setItem(this.scrollStorageKey, JSON.stringify({
      url: window.location.href,
      x: window.scrollX,
      y: window.scrollY
    }))
    window.Turbo.visit(window.location.href, { action: "replace" })
  }

  private restoreScroll() {
    const stored = window.sessionStorage.getItem(this.scrollStorageKey)
    if (!stored) return

    window.sessionStorage.removeItem(this.scrollStorageKey)
    const position = JSON.parse(stored) as { url: string, x: number, y: number }
    if (position.url !== window.location.href) return

    window.requestAnimationFrame(() => window.scrollTo(position.x, position.y))
  }
}

interface TurboFrameElement extends HTMLElement {
  src: string
  reload: () => Promise<void>
}

class BrokerageFormController extends Controller {
  static targets = ["adapter", "environment", "execution", "trading212", "trading212Required"]
  declare readonly adapterTarget: HTMLSelectElement
  declare readonly environmentTarget: HTMLSelectElement
  declare readonly executionTarget: HTMLInputElement
  declare readonly trading212Target: HTMLFieldSetElement
  declare readonly trading212RequiredTargets: HTMLInputElement[]

  connect() {
    this.change()
  }

  change() {
    const isTrading212 = this.adapterTarget.value === "trading212"
    const isDemo = this.environmentTarget.value === "paper"
    this.trading212Target.hidden = !isTrading212
    this.executionTarget.disabled = !isTrading212 || !isDemo
    if (this.executionTarget.disabled) this.executionTarget.checked = false
    this.trading212RequiredTargets.forEach((input) => {
      input.required = isTrading212 && input.dataset.configured !== "true"
    })
  }
}

class SubmitShortcutController extends Controller<HTMLFormElement> {
  submit(event: KeyboardEvent) {
    if (event.key !== "Enter" || (!event.metaKey && !event.ctrlKey)) return

    event.preventDefault()
    this.element.requestSubmit()
  }
}

class AutosubmitController extends Controller<HTMLFormElement> {
  submit() {
    this.element.requestSubmit()
  }
}

class FramePollController extends Controller {
  static values = { interval: { type: Number, default: 2_000 } }
  declare intervalValue: number
  private timer?: number
  private connected = false

  connect() {
    this.connected = true
    this.schedule()
  }

  disconnect() {
    this.connected = false
    if (this.timer) window.clearTimeout(this.timer)
  }

  private schedule() {
    this.timer = window.setTimeout(() => this.poll(), this.intervalValue)
  }

  private poll() {
    if (!this.connected) return
    if (document.visibilityState !== "visible") return this.schedule()

    const frame = this.element.closest("turbo-frame") as TurboFrameElement | null
    if (!frame) return

    const reload = frame.src ? frame.reload() : Promise.resolve(frame.src = window.location.href)
    reload.catch(() => {
      if (this.connected) this.schedule()
    })
  }
}

declare global {
  interface Window { Turbo: { visit: (url: string, options: { action: string }) => void } }
}

const application = Application.start()
application.register("refresh", RefreshController)
application.register("brokerage-form", BrokerageFormController)
application.register("submit-shortcut", SubmitShortcutController)
application.register("performance-chart", PerformanceChartController)
application.register("autosubmit", AutosubmitController)
application.register("frame-poll", FramePollController)
application.register("market-candles", MarketCandlesController)
